import { getEnvVariable } from '@/utils/get-env-variable';
import { logError } from '@/utils/logger';
import { getCheckoutRequestOptions } from '@/utils/checkout';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiRequestOptions extends RequestInit {
  headers?: Record<string, string>;
  parseAs?: 'json' | 'text' | 'blob';
}

export interface ApiError extends Error {
  status: number;
  info?: unknown;
}

export interface ApiClientOptions {
  // Default headers stamped onto every request this client makes (merged
  // under per-request and Authorization headers). Use this to attach the
  // checkout-context headers to the main API client while keeping other
  // clients (e.g. the PCI gateway) clean — sending extra custom headers
  // there would require them on that host's CORS allow-list.
  headers?: Record<string, string>;
}

export class ApiClient {
  private baseUrl: string;
  private headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  constructor(baseUrl: string, options: ApiClientOptions = {}) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    if (options.headers) this.setHeaders(options.headers);
  }

  public initilize(options: { token?: string }) {
    this.setHeader('Authorization', `Bearer ${options.token ?? ''}`);
  }

  public setHeader(key: string, value: string | null) {
    if (value) this.headers[key] = value;
    else delete this.headers[key];
  }

  public setHeaders(newHeaders: Record<string, string>) {
    this.headers = { ...this.headers, ...newHeaders };
  }

  public async request<T>(endpoint: string, method: HttpMethod = 'GET', options: ApiRequestOptions = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const { headers: optionsHeaders, ...restOptions } = options;
    const headers = { ...this.headers, ...(optionsHeaders || {}) };

    try {
      const response = await fetch(url, {
        ...restOptions,
        method,
        headers,
        body: options.body,
        credentials: 'include',
        mode: 'cors',
      });

      if (!response.ok) {
        const error: ApiError = new Error(`HTTP ${response.status}`) as ApiError;
        error.status = response.status;
        error.info = await parseErrorBody(response);
        throw error;
      }

      switch (options.parseAs) {
        case 'text':
          return (await response.text()) as T;
        case 'blob':
          return (await response.blob()) as T;
        default:
          return (await response.json()) as T;
      }
    } catch (error) {
      logError('API Request failed', error, {
        url,
        method,
        baseUrl: this.baseUrl,
        endpoint,
      });
      throw error;
    }
  }

  public get<T>(endpoint: string, options?: ApiRequestOptions) {
    return this.request<T>(endpoint, 'GET', options);
  }

  public post<T>(endpoint: string, body?: Record<string, unknown>, options?: ApiRequestOptions) {
    return this.request<T>(endpoint, 'POST', {
      ...options,
      body: JSON.stringify(body),
    });
  }

  public put<T>(endpoint: string, body?: unknown, options?: ApiRequestOptions) {
    return this.request<T>(endpoint, 'PUT', {
      ...options,
      body: JSON.stringify(body),
    });
  }

  public delete<T>(endpoint: string, options?: ApiRequestOptions) {
    return this.request<T>(endpoint, 'DELETE', options);
  }
}

/**
 * The non-2xx body, parsed as JSON when it is JSON and left as raw text when
 * it isn't. Callers read the gateway's `errorCode` off it, so losing the body
 * costs the only signal that distinguishes an ended session from a bad token —
 * the HTTP status can't tell them apart.
 *
 * Reads the stream exactly once. A failed `response.json()` still consumes the
 * body, so the previous `json()`-then-`text()` fallback threw `Body is
 * unusable` on any non-JSON error body and that TypeError replaced the
 * ApiError, taking `status` and `info` with it.
 */
async function parseErrorBody(response: Response): Promise<unknown> {
  let raw: string;
  try {
    raw = await response.text();
  } catch {
    return undefined;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

export const apiClient = new ApiClient(getEnvVariable('VITE_API_URL', 'https://api.appcharge.com'), {
  headers: getCheckoutRequestOptions().headers,
});
